import React from 'react';
import { Event, EventRegistration } from '../services/eventService';
import { toast } from 'react-toastify';
import styles from './EventCard.module.css';

interface EventCardProps {
  event: Event;
  registrations: EventRegistration[];
  userId: string | undefined;
  isAdmin: boolean;
  onCancelEvent: (eventId: string) => void;
}

export const EventCard: React.FC<EventCardProps> = ({ 
  event, 
  registrations, 
  userId, 
  isAdmin, 
  onCancelEvent 
}) => {
  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format date for the event date icon (day and month only)
  const formatEventDateIcon = (dateString: string): { day: string, month: string } => {
    const date = new Date(dateString);
    const day = date.getDate().toString();
    // Get month abbreviation in local format
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return { day, month };
  };

  // Check if user is registered for this event
  const isUserRegistered = Boolean(
    userId && registrations.some(reg => reg.Event_ID === event.Events_ID && reg.User_ID === userId)
  );

  // Get event status
  const getEventStatus = (): { status: string, messageLines: string[], className: string, toastType: string } => {
    const now = new Date();
    const eventDate = new Date(event.event_date);
    const registerEndDate = new Date(event.register_end_date);
    const registerStartDate = new Date(event.register_start_date);
    const voteEndDate = new Date(event.vote_end_date);
    
    if (now > eventDate) {
      return { 
        status: 'ended', 
        messageLines: ['Event has ended'], 
        className: 'bg-dark',
        toastType: 'dark'
      };
    } else if (now > voteEndDate) {
      return { 
        status: 'closed', 
        messageLines: ['Voting closed', 'Waiting for event'], 
        className: 'bg-warning',
        toastType: 'warning'
      };
    } else if (now > registerEndDate) {
      return { 
        status: 'active', 
        messageLines: ['Registration closed', 'Voting open'], 
        className: 'bg-success',
        toastType: 'success'
      };
    } else if (now > registerStartDate) {
      return { 
        status: 'registration', 
        messageLines: ['Registration open'], 
        className: 'bg-info',
        toastType: 'info'
      };
    } else {
      return { 
        status: 'not_started', 
        messageLines: ['Event not started'], 
        className: 'bg-secondary',
        toastType: 'default'
      };
    }
  };

  const eventStatus = getEventStatus();
  const eventDateIcon = formatEventDateIcon(event.event_date);

  // Show details modal
  const showEventDetails = () => {
    // Navigate to event details page
    window.location.href = `/events/${event.Events_ID}`;
  };

  // Show registration link as toast and copy to clipboard
  const showRegisterModal = (eventId: string) => {
    // Generate the registration link
    const registerLink = `${window.location.origin}/register/?event=${event.Events_ID}&verification_code=${event.Verification_Code}`;
    
    // Copy to clipboard and show toast
    navigator.clipboard.writeText(registerLink)
      .then(() => {
        toast.success('Registration link copied to clipboard!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      })
      .catch(err => {
        toast.error('Could not copy link to clipboard.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        toast.info(`Registration link: ${registerLink}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      });
  };

  // Show cancel confirmation as toast
  const showCancelModal = (eventId: string) => {
    toast.info(
      <div>
        <p>Are you sure you want to cancel your participation in this event?</p>
        <p><strong>Note:</strong> Only the event host can send you a new invitation.</p>
        <div>
          <button 
            className="btn btn-sm btn-danger me-2" 
            onClick={() => {
              onCancelEvent(eventId);
              toast.dismiss();
            }}
          >
            Yes, Cancel
          </button>
          <button 
            className="btn btn-sm btn-secondary" 
            onClick={() => toast.dismiss()}
          >
            No, Keep Registration
          </button>
        </div>
      </div>,
      { 
        autoClose: false, 
        closeOnClick: false,
        position: "top-center",
        hideProgressBar: true,
        draggable: false,
        className: 'toast-confirmation'
      }
    );
  };

  return (
    <div className="col col-12 col-md-6 col-xl-4 d-flex">
      <div className={`card event-card shadow-sm ${styles.eventCard} w-100`}>
        <div className="card-header d-flex align-items-center">
          <div className={`event-date me-3 text-center ${styles.eventDate}`}>
            <span className="d-block fw-bold fs-4">{eventDateIcon.day}</span>
            <small className="text-uppercase text-muted">{eventDateIcon.month}</small>
          </div>
          <h5 className="mb-0 flex-grow-1">{event.title}</h5>

          {/* Location Icon (nur anzeigen, wenn Location-Daten vorhanden sind) */}
          {event.location_name && event.street && event.city && event.country && (
            <button 
              className="btn btn-outline-secondary btn-sm ms-2 show-location"
              data-bs-toggle="modal"
              data-bs-target="#locationModal"
              data-location-name={event.location_name}
              data-street={event.street}
              data-house-number={event.house_number}
              data-postal-code={event.postal_code}
              data-city={event.city}
              data-country={event.country}
              title="Show location"
            >
              <i className="bi bi-pin-map"></i>
            </button>
          )}
        </div>
        
        <div className={`card-body ${styles.cardBody}`}>
          <p className="text-muted mb-3" style={{ whiteSpace: 'pre-line' }}>{event.sub_title}</p>

          {/* Status Anzeige as badge with toast-style color */}
          <div className="d-flex justify-content-center mt-auto">
            <span 
              className={`badge text-wrap text-center p-2 ${eventStatus.className} ${styles.badge}`}
            >
              {eventStatus.messageLines.map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < eventStatus.messageLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </span>
          </div>
        </div>

        {/* Footer mit einzeiliger Anordnung der Buttons */}
        <div className={`card-footer ${styles.cardFooter}`}>
          {isAdmin && (
            <>
              <a 
                href={`/events/${event.Events_ID}/view/`} 
                className={`btn btn-sm btn-outline-primary ${styles.actionButton}`}
                title="Edit Event"
              >
                <i className="bi bi-pencil-square me-1"></i> Edit
              </a>
              <button 
                className={`btn btn-sm btn-outline-info ${styles.actionButton}`}
                onClick={() => showRegisterModal(event.Events_ID)}
                title="Share Registration Link"
              >
                <i className="bi bi-share me-1"></i> Share
              </button>
            </>
          )}

          {isUserRegistered && eventStatus.status !== 'ended' && (
            <button 
              className={`btn btn-sm btn-outline-danger ${styles.actionButton}`}
              onClick={() => showCancelModal(event.Events_ID)}
              title="Cancel Registration"
            >
              <i className="bi bi-x-circle me-1"></i> Cancel
            </button>
          )}

          {isUserRegistered && eventStatus.status === 'active' && (
            <a 
              href={`/events/${event.Events_ID}/participants/`} 
              className={`btn btn-sm btn-outline-tertiary ${styles.actionButton}`}
              title="Choose Matches"
            >
              <i className="bi bi-people me-1"></i> Choose
            </a>
          )}

          {isUserRegistered && (eventStatus.status === 'closed' || eventStatus.status === 'ended') && (
            <a 
              href={`/events/${event.Events_ID}/rounds/`} 
              className={`btn btn-sm btn-outline-tertiary ${styles.actionButton}`}
              title="Watch the Matches"
            >
              <i className="bi bi-eye me-1"></i> Matches
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
